import * as CL from '@design-system/component-library';
import { AuthenticatedUserRole } from '../../generated/api/models.js';
import { Email } from '../../common/react-hook-form/fields/index.js';
import { FormProvider, useForm } from 'react-hook-form';
import {
  billingAddressTitleMsg,
  cancelMsg,
  confirmMsg,
  consumerOmaElisaMsg,
  editMsg,
  emailMsg,
  myEquipementInvoice,
  t,
} from '../../common/i18n';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { dsClass } from '../../common/constants/dsClasses';
import { findPersonBillingAccount } from '../../common/utils/stateUtils';
import { loadBillingAccounts, upsertPersonBillingAccount } from '../../selfservice/actions';
import { useAuth } from '../../public/site/AuthProvider';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import type { BillingAccount } from '../../generated/api/models.js';
import type { State } from '../../selfservice/common/store.js';

interface OmaLaskuDeviceFormValues {
  email: string;
}
interface OmalaskuDeviceInformationProps {
  billingAccount: BillingAccount;
}

const OmaLaskuDeviceForm = ({ billingAccount }: OmalaskuDeviceInformationProps) => {
  const dispatch = useDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const methods = useForm<OmaLaskuDeviceFormValues>({
    defaultValues: {
      email: billingAccount.billReceiverEmail || '',
    },
  });
  const { handleSubmit, getValues } = methods;

  const onSubmit = (values: OmaLaskuDeviceFormValues) => {
    dispatch(
      upsertPersonBillingAccount(
        {
          address: billingAccount.payerAddress,
          email: values.email ?? '',
        },
        billingAccount.billingAccountId,
        undefined
      )
    );
    setIsEdit(false);
    return true;
  };

  return (
    <FormProvider {...methods}>
      {isEdit ? (
        <CL.GridRow className={dsClass.MARGIN_TOP_6}>
          <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
            <Email name="email" readonly={!isEdit} required />
          </CL.GridCol>
        </CL.GridRow>
      ) : (
        <CL.GridRow>
          <CL.GridCol className={dsClass.PADDING_0} colWidthXS={4} colWidthS={6} colWidthL={12}>
            <h4 className={`${dsClass.H5} ${dsClass.MARGIN_TOP_6} ${dsClass.MARGIN_BOTTOM_2}`}>
              {t.PS5T(billingAddressTitleMsg)}
            </h4>
            <div>{t.OKYY(emailMsg)}</div>
            <div>{billingAccount.billReceiverEmail}</div>
          </CL.GridCol>
        </CL.GridRow>
      )}
      <CL.GridRow className={dsClass.MARGIN_TOP_4}>
        <CL.GridCol colWidthXS={4} className={dsClass.PADDING_0}>
          {isEdit ? (
            <>
              <CL.Button type="submit" onClick={handleSubmit(() => onSubmit(getValues()))}>
                {t.QVYK(confirmMsg)}
              </CL.Button>
              <CL.Button type="button" color="link" className={dsClass.MARGIN_LEFT_4} onClick={() => setIsEdit(false)}>
                {t.B2V1(cancelMsg)}
              </CL.Button>
            </>
          ) : (
            <CL.Button type="button" color="light" onClick={() => setIsEdit(true)}>
              {t.NVPK(editMsg)}
            </CL.Button>
          )}
        </CL.GridCol>
      </CL.GridRow>
    </FormProvider>
  );
};

const DeviceInfoContent = ({ billingAccount }: OmalaskuDeviceInformationProps) => {
  return (
    <>
      {t.AUI3('You can view and pay your invoice in')} &nbsp;
      <CL.Link className={dsClass.FONT_SIZE_MEDIUM} linkTarget="_blank" linkHref="https://verkkoasiointi.elisa.fi">
        {t.QE2O(consumerOmaElisaMsg)}
        <CL.Icon icon="external-link" size="s" type="regular" color="blue-600" />
      </CL.Link>
      <OmaLaskuDeviceForm billingAccount={billingAccount} />
    </>
  );
};

const NoDeviceInfoContent = () => {
  return (
    <CL.Link
      className={dsClass.FONT_SIZE_MEDIUM}
      linkTarget="_blank"
      linkHref="https://yrityksille.elisa.fi/oma-lasku-tyosuhdeliittymaan"
    >
      {t.TFMQ('More info about Oma Laitelasku')}
    </CL.Link>
  );
};

export const OmalaskuDeviceInformation = () => {
  const billingAccounts = useSelector((state: State) => state.selfservice?.billingAccounts, deepEqual);
  const { authenticatedUser } = useAuth();
  const billingAccount = billingAccounts ? findPersonBillingAccount(billingAccounts) : undefined;
  const isPunchoutUser = authenticatedUser?.userRole === AuthenticatedUserRole.PUNCHOUT_USER;
  const dispatch = useDispatch();

  useEffect(() => {
    if (authenticatedUser?.businessId) {
      dispatch(loadBillingAccounts());
    }
  }, [dispatch, authenticatedUser?.businessId]);

  return (
    <>
      <div className={dsClass.MARGIN_TOP_6}>
        <h3 className={dsClass.DESCRIPTION_TITLE}>{t.M1N1(myEquipementInvoice)}</h3>
        <p>
          {t.NY4L(
            `With Elisa Oma Laitelasku, you can choose a device that is better than your employer's standard device. You pay the difference in price each month in a separate invoice.`
          )}
        </p>
      </div>
      {billingAccount && !isPunchoutUser ? (
        <DeviceInfoContent billingAccount={billingAccount} />
      ) : (
        <NoDeviceInfoContent />
      )}
    </>
  );
};
